import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Разговор обрывается сам по двум условиям — это НЕ квота, а страховка от
// забытой вкладки. Живой разговор тарифицируется по минутам аудио, и сессия,
// оставленная открытой, тратит деньги, пока микрофон ловит шум комнаты.
//
// MAX_SESSION_SEC — потолок длительности. Настоящий IELTS Speaking идёт
// 11–14 минут, так что пятнадцати хватает на полный разговор.
// IDLE_STOP_SEC — тишина со стороны ученика. Если он молчит три минуты,
// он ушёл: экзаменатор своей репликой этот счётчик не сбрасывает.
const MAX_SESSION_SEC = 15 * 60;
const IDLE_STOP_SEC = 3 * 60;

import {
  ieltsSpeakingApi,
  type RealtimeTokenResult,
  type SpeakingCueCard,
  type SpeakingSession,
  type SpeakingTurn,
} from '@/api/ielts';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { notifyError, tapMedium } from '@/lib/haptics';

/**
 * LiveSpeakingSession — the PRIMARY IELTS Speaking experience (Feature #3b-live).
 *
 * The student holds a CONTINUOUS speech-to-speech conversation with an AI IELTS
 * examiner: walk in, press Start, and just talk until you press Stop. There is
 * NO per-turn record/stop — the OpenAI Realtime API's server-side VAD detects
 * when the student starts/stops speaking (and supports barge-in).
 *
 * Flow:
 *   1. Fetch a short-lived ephemeral secret from our backend (the real API key
 *      never reaches the browser).
 *   2. getUserMedia(mic) → RTCPeerConnection → SDP offer → POST to OpenAI's
 *      /v1/realtime/calls → SDP answer → connected.
 *   3. Remote AI audio track plays through a hidden <audio> element.
 *   4. A data channel streams events: input-audio transcription (student) and
 *      output-audio transcript (examiner) build a live transcript; VAD events
 *      drive the listening / speaking indicator.
 *   5. On Stop, the assembled transcript is graded through the existing IELTS
 *      band-scoring endpoint (history preserved).
 *
 * The old turn-based flow remains reachable as a fallback (see the page toggle).
 */

type Phase = 'idle' | 'connecting' | 'live' | 'grading' | 'error';
type AIState = 'idle' | 'listening' | 'speaking';

type LiveTurn = SpeakingTurn & { id: string };

/** Mirror of the backend part thresholds so the graded transcript is labelled. */
function partForStudentAnswers(prior: number): 1 | 2 | 3 {
  if (prior < 4) return 1;
  if (prior < 5) return 2;
  return 3;
}

/**
 * The ephemeral Realtime secret is short-lived. If it is already expired or
 * about to expire when we reach the WebRTC SDP exchange (clock skew, a very
 * short TTL, or the student lingered on the Start button), the exchange fails
 * with an expired secret. Treat anything with under this much life left as
 * "near expiry" and re-mint once before connecting.
 */
const TOKEN_MIN_TTL_MS = 10_000;
function isTokenNearExpiry(expiresAtUnixSeconds: number | undefined): boolean {
  if (!expiresAtUnixSeconds) return false; // no expiry info → trust the token
  return expiresAtUnixSeconds * 1000 - Date.now() < TOKEN_MIN_TTL_MS;
}

export function LiveSpeakingSession({
  cueCardId,
  onGraded,
  onExit,
}: {
  cueCardId?: string | null;
  onGraded: (session: SpeakingSession) => void;
  onExit: () => void;
}) {
  const { t, i18n } = useTranslation();
  // Язык ошибок — экрана, а не интерфейса: в моке это английский (`lib/mockEnglish.ts`),
  // а `humanizeError` сам берёт тексты из общего экземпляра.
  const errLng = i18n.language;

  const [phase, setPhase] = useState<Phase>('idle');
  const [aiState, setAIState] = useState<AIState>('idle');
  // Микрофон можно заглушить: экзаменатор слушает непрерывно, и посторонний
  // шум — разговор в комнате, телевизор, улица — он принимает за речь
  // кандидата. Без выключателя ученик не может взять паузу, а мы платим
  // за каждую секунду такой «речи».
  const [micOn, setMicOn] = useState(true);
  // Сколько секунд осталось до принудительного завершения. Показывается
  // ученику, чтобы отсечка не была неожиданностью.
  const [leftSec, setLeftSec] = useState(MAX_SESSION_SEC);
  const [error, setError] = useState<string | null>(null);
  const [cueCard, setCueCard] = useState<SpeakingCueCard | null>(null);
  const [turns, setTurns] = useState<LiveTurn[]>([]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  // Когда ученик говорил в последний раз — по этому определяем, что он ушёл.
  const lastSpeechRef = useRef<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Accumulates streaming examiner transcript deltas keyed by response/item id.
  const examinerBufRef = useRef<Record<string, string>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const resolvedCueIdRef = useRef<string | null>(null);
  // The mint-time session row id, echoed back on grade so the same row is
  // finished (keeps the backend per-student hourly cap accurate).
  const resolvedSessionIdRef = useRef<string | null>(null);
  // Wall-clock start of the live conversation (ms), captured when the session
  // goes live. Used to report the session's real duration on grade so the admin
  // cost panel can price the Realtime audio minutes we never see server-side.
  const liveStartRef = useRef<number | null>(null);
  // The last minted (but not-yet-consumed) realtime token. Reused across connect
  // retries so a failed SDP exchange / mic-denied error doesn't mint a brand-new
  // PAID session on every Start press. Cleared once a session actually goes live.
  const tokenRef = useRef<RealtimeTokenResult | null>(null);
  // Set before a DELIBERATE teardown (Stop/grade or unmount) so the peer
  // connection's own 'closed' event isn't mistaken for a mid-session failure.
  const intentionalCloseRef = useRef(false);
  const turnsRef = useRef<LiveTurn[]>([]);

  // Keep a ref of turns for teardown-time grading without stale closures.
  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length]);

  const pushStudentTurn = useCallback((content: string) => {
    const text = content.trim();
    if (!text) return;
    setTurns((prev) => {
      const priorStudent = prev.filter((x) => x.role === 'student').length;
      const part = partForStudentAnswers(priorStudent);
      return [...prev, { id: `s-${Date.now()}-${prev.length}`, role: 'student', content: text, part }];
    });
  }, []);

  const pushExaminerTurn = useCallback((content: string) => {
    const text = content.trim();
    if (!text) return;
    setTurns((prev) => {
      const priorStudent = prev.filter((x) => x.role === 'student').length;
      const part = partForStudentAnswers(priorStudent);
      return [...prev, { id: `e-${Date.now()}-${prev.length}`, role: 'examiner', content: text, part }];
    });
  }, []);

  // -------- Realtime event handling (data channel) --------
  const handleEvent = useCallback(
    (raw: string) => {
      let ev: Record<string, unknown>;
      try {
        ev = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        return;
      }
      const type = String(ev.type ?? '');

      switch (type) {
        // Student started/stopped talking (server VAD).
        case 'input_audio_buffer.speech_started':
          setAIState('listening');
          lastSpeechRef.current = Date.now();
          break;

        // Examiner (assistant) audio is being produced.
        case 'response.created':
        case 'response.output_audio.delta':
        case 'response.audio.delta':
          setAIState('speaking');
          break;

        case 'response.done':
        case 'response.output_audio.done':
        case 'response.audio.done':
          setAIState('listening');
          break;

        // Student's speech transcription (final).
        case 'conversation.item.input_audio_transcription.completed': {
          const transcript = String(ev.transcript ?? '');
          if (transcript.trim()) pushStudentTurn(transcript);
          break;
        }

        // Examiner transcript — streamed as deltas, committed on done. Handle
        // both the GA (`output_audio_transcript`) and legacy (`audio_transcript`)
        // event names.
        case 'response.output_audio_transcript.delta':
        case 'response.audio_transcript.delta': {
          const key = String(ev.item_id ?? ev.response_id ?? 'cur');
          const delta = String(ev.delta ?? '');
          examinerBufRef.current[key] = (examinerBufRef.current[key] ?? '') + delta;
          break;
        }
        case 'response.output_audio_transcript.done':
        case 'response.audio_transcript.done': {
          const key = String(ev.item_id ?? ev.response_id ?? 'cur');
          const full = String(ev.transcript ?? examinerBufRef.current[key] ?? '');
          delete examinerBufRef.current[key];
          if (full.trim()) pushExaminerTurn(full);
          break;
        }

        case 'error': {
          // ⚠️ Текст ошибки OpenAI Realtime английский и внутренний — только через `humanizeError` (25.09.2026).
          const errObj = ev.error as { message?: string } | undefined;
          if (errObj?.message) setError(humanizeError(errObj, errLng));
          break;
        }
        default:
          break;
      }
    },
    [pushStudentTurn, pushExaminerTurn, errLng],
  );

  // -------- Teardown --------
  const teardown = useCallback(() => {
    try {
      dcRef.current?.close();
    } catch {
      /* noop */
    }
    dcRef.current = null;
    try {
      pcRef.current?.close();
    } catch {
      /* noop */
    }
    pcRef.current = null;
    micRef.current?.getTracks().forEach((tr) => tr.stop());
    micRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }
    setAIState('idle');
  }, []);

  useEffect(
    () => () => {
      // Unmount is a deliberate close, not a connection failure.
      intentionalCloseRef.current = true;
      teardown();
    },
    [teardown],
  );

  // -------- Start the live session --------
  const start = useCallback(async () => {
    setError(null);
    // Fresh session: any prior deliberate-close marker must not suppress this
    // session's failure detection.
    intentionalCloseRef.current = false;

    // Feature-detect: WebRTC + mic are required.
    if (
      typeof RTCPeerConnection === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setError(t('ielts.speaking.live.unsupported'));
      setPhase('error');
      return;
    }

    setPhase('connecting');
    try {
      // 1) Ephemeral secret + session config from our backend.
      // Reuse a still-valid token from a previous (failed) attempt instead of
      // minting a new PAID session on every retry. Only mint when we have none
      // or the stored one is at/near expiry — and mint AT MOST once (the old
      // code always minted, then double-minted on near-expiry, burning two
      // sessions against the hourly cap).
      const mintBody = cueCardId ? { cue_card_id: cueCardId } : undefined;
      let token = tokenRef.current;
      if (!token || isTokenNearExpiry(token.expires_at)) {
        token = await ieltsSpeakingApi.realtimeToken(mintBody);
        tokenRef.current = token;
      }
      setCueCard(token.cue_card ?? null);
      resolvedCueIdRef.current = token.cue_card?.id ?? cueCardId ?? null;
      resolvedSessionIdRef.current = token.session_id ?? null;

      // 2) Mic.
      let mic: MediaStream;
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        notifyError(); // микрофон недоступен — разговор не начнётся
        setError(t('ielts.speaking.live.micDenied'));
        setPhase('error');
        return;
      }
      micRef.current = mic;
      tapMedium(); // микрофон открыт, разговор пошёл
      setMicOn(true); // новая сессия всегда начинается с включённого микрофона

      // 3) Peer connection.
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // Remote AI audio → hidden <audio>.
      pc.ontrack = (e) => {
        if (audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => {
            /* autoplay may require a gesture; Start is a gesture, so usually ok */
          });
        }
      };
      pc.onconnectionstatechange = () => {
        // Ignore the 'closed' event our own teardown (Stop/grade, unmount)
        // produces — that is not a failure.
        if (intentionalCloseRef.current) return;
        const st = pc.connectionState;
        if (st === 'failed' || st === 'disconnected' || st === 'closed') {
          // Mid-session WebRTC failure: fully tear down (close the peer
          // connection, stop mic tracks, detach the audio sink) and reset the
          // UI to a restartable error state instead of leaving a dead,
          // resource-leaking session with a stuck "listening" indicator.
          setError((prev) => prev ?? t('ielts.speaking.live.connectionLost'));
          teardown();
          setPhase('error');
        }
      };

      mic.getTracks().forEach((tr) => pc.addTrack(tr, mic));

      // Data channel for events (transcripts + VAD state).
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = (e) => handleEvent(e.data as string);

      // Экзаменатор здоровается ПЕРВЫМ, как на настоящем экзамене. Без этого
      // модель молча ждала, пока заговорит кандидат: ученик видел «слушаю» и
      // не понимал, что делать. Реальный экзаменатор всегда открывает
      // разговор сам — представляется и задаёт первый вопрос Part 1.
      dc.onopen = () => {
        dc.send(JSON.stringify({ type: 'response.create' }));
      };

      // 4) SDP offer → OpenAI → answer.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // No ?model= query param: with the ephemeral-secret flow the model is
      // baked into the session on the backend at mint time, so the SDP exchange
      // only needs the Bearer secret. (Verified against the OpenAI Realtime GA
      // WebRTC docs, developers.openai.com, fetched 2026-07-02.)
      const sdpResp = await fetch(token.call_url, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${token.client_secret}`,
          'Content-Type': 'application/sdp',
        },
      });
      if (!sdpResp.ok) {
        // Статусом, а не английской фразой: `humanizeError` сделает из 5xx «Что-то пошло не так».
        throw Object.assign(new Error(''), { status: sdpResp.status });
      }
      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });

      // Session is now live: this token/session is consumed. Drop it so the next
      // Start (after Stop/grade or a mid-session drop) mints a fresh session
      // instead of reusing a spent one.
      tokenRef.current = null;
      liveStartRef.current = Date.now();
      lastSpeechRef.current = Date.now();
      setLeftSec(MAX_SESSION_SEC);
      setPhase('live');
      setAIState('listening');
    } catch (e) {
      teardown();
      setError(humanizeError(e, errLng));
      setPhase('error');
    }
  }, [cueCardId, handleEvent, t, teardown, errLng]);

  // toggleMic глушит дорожку, а НЕ останавливает её: остановленную нельзя
  // включить обратно, пришлось бы пересобирать соединение. При enabled=false
  // в канал уходит тишина, и распознавание речи не срабатывает.
  const toggleMic = useCallback(() => {
    const stream = micRef.current;
    if (!stream) return;
    const next = !micOn;
    stream.getAudioTracks().forEach((tr) => {
      tr.enabled = next;
    });
    setMicOn(next);
  }, [micOn]);

  // -------- Stop + grade --------
  const stopAndGrade = useCallback(async () => {
    // Deliberate close: suppress the failure path in onconnectionstatechange.
    intentionalCloseRef.current = true;
    teardown();
    const collected = turnsRef.current;
    const studentTurns = collected.filter((x) => x.role === 'student');
    if (studentTurns.length === 0) {
      // Nothing to grade — just exit.
      onExit();
      return;
    }
    setPhase('grading');
    try {
      const durationSeconds = liveStartRef.current
        ? Math.round((Date.now() - liveStartRef.current) / 1000)
        : 0;
      const session = await ieltsSpeakingApi.realtimeGrade({
        session_id: resolvedSessionIdRef.current ?? undefined,
        cue_card_id: resolvedCueIdRef.current ?? undefined,
        duration_seconds: durationSeconds,
        turns: collected.map(({ role, content, part }) => ({ role, content, part })),
      });
      onGraded(session);
    } catch (e) {
      setError(humanizeError(e, errLng));
      setPhase('error');
    }
  }, [onExit, onGraded, teardown, errLng]);

  // Автоотсечка. Тикает раз в секунду, пока разговор идёт.
  //
  // Два независимых условия, и оба нужны: по времени — на случай, если ученик
  // заговорился; по тишине — если он просто ушёл, оставив вкладку. Второе
  // важнее: забытая сессия тратит деньги, пока микрофон ловит шум комнаты,
  // и сама никогда не закончится.
  useEffect(() => {
    if (phase !== 'live') return;
    const startedAt = liveStartRef.current || Date.now();
    if (!lastSpeechRef.current) lastSpeechRef.current = startedAt;

    const id = window.setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startedAt) / 1000);
      setLeftSec(Math.max(0, MAX_SESSION_SEC - elapsed));

      const silent = Math.floor((now - lastSpeechRef.current) / 1000);
      if (elapsed >= MAX_SESSION_SEC || silent >= IDLE_STOP_SEC) {
        window.clearInterval(id);
        // Ответы ученика уже собраны — завершаем как обычную остановку,
        // с оценкой, а не обрывом: сказанное не должно пропасть.
        void stopAndGrade();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, stopAndGrade]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const connecting = phase === 'connecting';
  const live = phase === 'live';
  const grading = phase === 'grading';

  return (
    <div className="space-y-4">
      {/* Hidden sink for the AI voice. */}
      <audio ref={audioRef} autoPlay className="hidden" />

      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onExit} disabled={connecting || grading}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.speaking.exit')}
        </Button>
        <StatusPill phase={phase} aiState={aiState} />
      </div>

      {cueCard && live && <CuePanel card={cueCard} />}

      <Card className="grid place-items-center gap-6 py-10">
        <MicOrb phase={phase} aiState={aiState} />

        <p className="max-w-md text-center text-sm text-on-surface-variant">
          {phase === 'idle' && t('ielts.speaking.live.introHint')}
          {connecting && t('ielts.speaking.live.connecting')}
          {live && aiState === 'speaking' && t('ielts.speaking.live.aiSpeaking')}
          {live && aiState !== 'speaking' && !micOn && t('ielts.speaking.live.micMuted')}
          {live && aiState !== 'speaking' && micOn && t('ielts.speaking.live.yourTurn')}
          {grading && t('ielts.speaking.live.grading')}
          {phase === 'error' && (error ?? t('ielts.speaking.live.genericError'))}
        </p>

        {error && phase !== 'error' && <p className="text-sm text-error">{error}</p>}

        {live && (
          <p className="font-mono text-sm tabular-nums text-on-surface-variant">
            {t('ielts.speaking.live.timeLeft')}{' '}
            <span className={leftSec <= 60 ? 'font-bold text-error' : ''}>
              {Math.floor(leftSec / 60)}:{String(leftSec % 60).padStart(2, '0')}
            </span>
          </p>
        )}

        <div className="flex gap-3">
          {(phase === 'idle' || phase === 'error') && (
            <Button onClick={start} loading={connecting}>
              <span className="material-symbols-outlined">graphic_eq</span>
              {t('ielts.speaking.live.start')}
            </Button>
          )}
          {live && (
            <Button variant={micOn ? 'ghost' : 'primary'} onClick={toggleMic}>
              <span className="material-symbols-outlined">{micOn ? 'mic' : 'mic_off'}</span>
              {micOn ? t('ielts.speaking.live.muteMic') : t('ielts.speaking.live.unmuteMic')}
            </Button>
          )}
          {live && (
            <Button variant="danger" onClick={stopAndGrade}>
              <span className="material-symbols-outlined">stop_circle</span>
              {t('ielts.speaking.live.stop')}
            </Button>
          )}
          {grading && (
            <Button loading disabled>
              {t('ielts.speaking.live.grading')}
            </Button>
          )}
        </div>
      </Card>

      {turns.length > 0 && (
        <Card className="p-0">
          <CardTitle className="px-4 pt-4">{t('ielts.speaking.transcriptTitle')}</CardTitle>
          <div ref={scrollRef} className="max-h-72 space-y-2 overflow-y-auto p-4">
            {turns.map((turn) => (
              <div
                key={turn.id}
                className={cn('flex', turn.role === 'student' ? 'justify-end' : 'justify-start')}
              >
                <div className={cn('max-w-[80%]', turn.role === 'student' ? 'text-right' : 'text-left')}>
                  <span className="text-[10px] font-semibold uppercase tracking-label text-on-surface-variant/70">
                    {turn.role === 'student'
                      ? t('ielts.speaking.you')
                      : t('ielts.speaking.examiner')}
                  </span>
                  <div
                    className={cn(
                      'mt-0.5 rounded-card px-3 py-2 text-sm',
                      turn.role === 'student'
                        ? 'bg-primary-container text-on-primary-container'
                        : 'bg-surface-container-low text-on-surface',
                    )}
                  >
                    {turn.content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function StatusPill({ phase, aiState }: { phase: Phase; aiState: AIState }) {
  const { t } = useTranslation();
  if (phase === 'connecting')
    return <Pill variant="warning">{t('ielts.speaking.live.connecting')}</Pill>;
  if (phase === 'grading')
    return <Pill variant="primary">{t('ielts.speaking.live.grading')}</Pill>;
  if (phase === 'live')
    return aiState === 'speaking' ? (
      <Pill variant="primary">{t('ielts.speaking.live.aiSpeaking')}</Pill>
    ) : (
      <Pill variant="success">{t('ielts.speaking.live.listening')}</Pill>
    );
  return <Pill variant="neutral">{t('ielts.speaking.live.badge')}</Pill>;
}

function MicOrb({ phase, aiState }: { phase: Phase; aiState: AIState }) {
  const active = phase === 'live';
  const speaking = active && aiState === 'speaking';
  return (
    <div
      className={cn(
        'grid h-28 w-28 place-items-center rounded-full transition',
        speaking
          ? 'bg-primary text-on-primary'
          : active
            ? 'bg-tertiary-container text-on-surface'
            : 'bg-surface-container-low text-on-surface-variant',
        (active || phase === 'connecting') && 'animate-pulse',
      )}
    >
      <span className="material-symbols-outlined text-5xl">
        {speaking ? 'record_voice_over' : phase === 'connecting' ? 'sync' : 'mic'}
      </span>
    </div>
  );
}

function CuePanel({ card }: { card: SpeakingCueCard }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardTitle>{card.title || t('ielts.speaking.cueCardTitle')}</CardTitle>
      {card.cue_text && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-on-surface-variant">{card.cue_text}</p>
      )}
    </Card>
  );
}
